// Page (website builder) block model — the web-layout counterpart of
// newsletterBlocks.js. It deliberately REUSES the leaf block factories
// (text/image/button/line/spacer) from newsletterBlocks so the email and page
// editors share one inspector and one set of leaf shapes; it only adds the
// nesting containers (section → row → column) and web-only leaves (hero,
// product-embed, product-grid) on top.
//
// Why a separate module (not an extension of the newsletter union): the email
// renderers are table/inline-style/CAN-SPAM specific and must never have to
// understand web-only block types. Pages render via a React/CSS path
// (src/features/site/PageRenderer.tsx), not wrapNewsletterHtml.
//
// A PageDoc is a tree:
//   PageDoc { slug, title, seoDescription, theme, sections: Section[] }
//   Section { type:'section', ...bg, rows: Row[] }
//   Row     { type:'row', columns: Column[] }
//   Column  { type:'column', widthFraction, blocks: LeafBlock[] }
//   LeafBlock = newsletter text/image/button/line/spacer | hero | product-embed | product-grid

import { createBlock as createLeafBlock } from './newsletterBlocks.js';

// Leaf block types addable inside a column. NOTE: 'image' and 'video' are
// intentionally NOT here — the 'library' block ("File/Folder/Embed") is the
// catch-all: file mode covers images + every file type, embed mode covers
// YouTube/Vimeo. The `image`/`video` leaves are still RENDERED/edited for
// backward compatibility (see createPageLeaf + the renderers + migrateLegacyBlocks
// which upgrades old video blocks), they just can't be added anew.
export const ADDABLE_PAGE_BLOCK_TYPES = Object.freeze([
  'text', 'library', 'button', 'line', 'spacer', 'hero', 'product-embed', 'product-grid', 'mini-nav',
]);

// Friendly labels for the "add block" menus (the raw type id is the fallback).
// Keep 'image' here so any legacy image blocks still show a clean label.
export const PAGE_BLOCK_LABELS = Object.freeze({
  text: 'Text',
  image: 'Image',
  video: 'Video',
  library: 'File/Folder/Embed',
  button: 'Button',
  line: 'Line',
  spacer: 'Spacer',
  hero: 'Hero',
  'product-embed': 'Product',
  'product-grid': 'Product grid',
  'mini-nav': 'Mini-nav',
});

export function pageBlockLabel(type) {
  return PAGE_BLOCK_LABELS[type] || type;
}

const NEWSLETTER_LEAF_TYPES = Object.freeze(['text', 'image', 'button', 'line', 'spacer']);

export const PAGE_DEFAULT_THEME = Object.freeze({
  pageBgColor: '#0a0a0a',
  contentBgColor: '#0a0a0a',
  textColor: '#e4e4e7',
  fontPackId: 'quicksand',
  baseFontSize: 16,
  linkColor: '#60a5fa',
  accentColor: '#3b82f6',
  // Max width (px) of centered content inside a non-full-bleed section. Caps the
  // content column site-wide so ultra-wide screens don't stretch the layout.
  contentMaxWidth: 1200,
  // Horizontal gutter (px) between the content column and the screen edge,
  // applied site-wide to non-full-bleed sections (clamped on mobile).
  contentPaddingX: 24,
  // Default text styling applied site-wide (nav + content) and inherited by every
  // block unless it overrides locally. 400=normal, 700=bold.
  baseFontWeight: 400,
  baseItalic: false,
  // Site-wide heading sizes (px) for H1/H2/H3 authored inside rich-text blocks,
  // so headings render at one consistent scale on every page.
  h1Size: 32,
  h2Size: 24,
  h3Size: 20,
  // Product-header styling, shared by EVERY product header (detail embed) on the
  // site so all product pages stay consistent. Defaults reproduce the classic
  // look exactly, so themes saved before these fields render unchanged.
  productPaddingY: 0,
  productPaddingX: 0,
  productTitleSize: 18,
  productPriceSize: 20,
  productDescSize: 14,
  productBtnLabel: '', // '' = "Add to Cart"
  productBtnBgColor: '', // '' = default cyan
  productBtnTextColor: '', // '' = white
  productBtnFontSize: 14,
  productBtnRadius: 8,
  productBtnPaddingY: 12,
  // Gallery main image: 'contain' letterboxes on the gallery background;
  // 'cover' fills the area (cropping) so there are no bars. An aspect ratio
  // ('1:1' | '4:3' | '3:2' | '16:9', '' = natural) fixes the gallery shape.
  productImageFit: 'contain',
  productImageAspect: '',
  productImageMaxH: 400,
  // Site-wide disclaimer (rich HTML) shown right above the Add-to-Cart button on
  // EVERY product page (import/shipping notes etc.). '' = hidden.
  productDisclaimerHtml: '',
});

/** The theme's H1/H2/H3 pixel sizes, defaulted for older stored themes that
 *  predate the fields. One source for the editor surface and the published
 *  render so the two can never disagree. */
export function themeHeadingSizes(theme) {
  return {
    h1: Number(theme?.h1Size) || PAGE_DEFAULT_THEME.h1Size,
    h2: Number(theme?.h2Size) || PAGE_DEFAULT_THEME.h2Size,
    h3: Number(theme?.h3Size) || PAGE_DEFAULT_THEME.h3Size,
  };
}

/** The theme's shared product-header style (padding, type sizes, add-to-cart
 *  button, gallery image fit), defaulted for stored themes that predate the
 *  fields. One source for the editor canvas and the published render so every
 *  product header on the site looks the same. */
export function themeProductHeaderStyle(theme) {
  // 0 is a valid value for the paddings, so default on non-finite (not falsy).
  const num = (v, def) => (Number.isFinite(Number(v)) && v !== '' && v !== null && v !== undefined ? Number(v) : def);
  const d = PAGE_DEFAULT_THEME;
  return {
    paddingY: num(theme?.productPaddingY, d.productPaddingY),
    paddingX: num(theme?.productPaddingX, d.productPaddingX),
    titleSize: num(theme?.productTitleSize, d.productTitleSize),
    priceSize: num(theme?.productPriceSize, d.productPriceSize),
    descSize: num(theme?.productDescSize, d.productDescSize),
    btnLabel: String(theme?.productBtnLabel ?? d.productBtnLabel),
    btnBgColor: String(theme?.productBtnBgColor ?? d.productBtnBgColor),
    btnTextColor: String(theme?.productBtnTextColor ?? d.productBtnTextColor),
    btnFontSize: num(theme?.productBtnFontSize, d.productBtnFontSize),
    btnRadius: num(theme?.productBtnRadius, d.productBtnRadius),
    btnPaddingY: num(theme?.productBtnPaddingY, d.productBtnPaddingY),
    imageFit: theme?.productImageFit === 'cover' ? 'cover' : 'contain',
    imageAspect: String(theme?.productImageAspect ?? d.productImageAspect),
    imageMaxH: num(theme?.productImageMaxH, d.productImageMaxH),
    disclaimerHtml: String(theme?.productDisclaimerHtml ?? d.productDisclaimerHtml),
  };
}

let idCounter = 0;
function genId(prefix) {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter.toString(36)}`;
}

// ── Leaf factories ──────────────────────────────────────────────────────────

// Build a single leaf block. Reuses the newsletter factory for the shared five
// and defines the web-only leaves natively.
export function createPageLeaf(type) {
  if (NEWSLETTER_LEAF_TYPES.includes(type)) {
    const leaf = createLeafBlock(type);
    // Web pages aren't 600px email columns — drop the email-only side padding so
    // leaves fill their column; spacing is owned by the section/column instead.
    if (type === 'text') { leaf.paddingX = 0; leaf.textColor = '#e4e4e7'; }
    if (type === 'image') { leaf.href = ''; }
    return leaf;
  }
  switch (type) {
    case 'video':
      return {
        id: genId('video'), type: 'video',
        provider: 'file', // 'file' (library upload) | 'youtube'
        src: '', // file: public media URL; youtube: 11-char id
        poster: '',
        autoplay: false,
        loop: false,
        muted: false,
        controls: true,
        width: 100,
        align: 'center',
      };
    case 'library':
      return {
        id: genId('library'), type: 'library',
        mode: 'file', // 'file' (one media upload) | 'folder' (public folder) | 'embed' (YouTube/Vimeo)
        // file mode
        src: '', // public media URL (/api/media-uploads/:id)
        filename: '', // for ModelPreview's obj/stl detection + download name
        mime: '',
        autoRotate: false, // 3D only
        bgColor: '', // 3D only ('' = default checkerboard)
        // folder mode
        folderId: '', // the designated public ('shared_global') root folder
        folderName: '',
        layout: 'grid', // 'grid' | 'browser'
        columns: 3,
        dlButtonColor: '', // download-folder button color ('' = default blue)
        breadcrumbFontSize: 12, // breadcrumb font size (px)
        // embed mode (absorbed from the old standalone video block)
        embedProvider: 'youtube', // 'youtube' | 'vimeo'
        embedId: '', // youtube: 11-char id; vimeo: numeric id (or id/hash)
        // Playback options — apply to BOTH an embed and a picked video FILE.
        autoplay: false,
        loop: false,
        muted: false, // file video only (embeds force-mute when autoplaying)
        controls: true,
        // shared
        width: 100,
        align: 'center',
        paddingY: 0, // vertical padding (px) around the block
        paddingX: 0, // horizontal padding (px)
        // file mode, image kind only (absorbed from the old standalone image block)
        href: '', // click-through link
        caption: '',
        showCaption: false,
        alt: '',
        sectionColor: 'transparent',
      };
    case 'hero':
      return {
        id: genId('hero'), type: 'hero',
        heading: 'Your headline here',
        subheading: 'A short supporting line that sells the vibe.',
        bgImage: '',
        overlayColor: '#000000',
        overlayOpacity: 0.4,
        textColor: '#ffffff',
        align: 'center',
        minHeight: 460,
        ctaLabel: 'Shop now',
        ctaHref: '',
        ctaStyleVariant: 'rounded',
      };
    case 'product-embed':
      return {
        id: genId('prod'), type: 'product-embed',
        handle: '',
        variant: 'detail', // 'card' | 'detail' | 'fullscreen'
        // Detail-layout authoring (detail variant only). All optional so older
        // saved docs render unchanged; read sites default these at use time.
        layout: 'stacked', // 'stacked' | 'gallery-left' | 'gallery-right'
        showShopifyImages: true, // toggle the live Shopify product images on/off
        showVideos: true, // play video gallery items inline in card/fullscreen variants
        extraImages: [], // our own B2 public images mixed into the gallery: { id, src, alt }
        galleryOrder: [], // ordered image keys: 'shopify:<idx>' | extra image id
        // Per-variant hero image: variant id → gallery key. When a shopper picks
        // that variant the gallery advances to this image. One image per variant.
        variantHeroImages: {},
        // Gallery slideshow: when on, the main image auto-advances every N seconds.
        galleryAutoAdvance: false,
        galleryAutoAdvanceSeconds: 4,
        // Detail background color ('' = the default dark theme). Controls both the
        // detail surface and the image letterbox area (otherwise a fixed grey).
        bgColor: '',
      };
    case 'product-grid':
      return {
        id: genId('grid'), type: 'product-grid',
        handles: 'all', // 'all' | string[] of product handles
        columns: 3,
        showVideos: true, // play video gallery items inline in the cards
      };
    case 'mini-nav':
      return {
        id: genId('mininav'), type: 'mini-nav',
        links: [], // { id, label, target } — target is a RELATIVE page slug / product handle / 'home' (or absolute URL); renderer resolves to /g/:orgSlug/… at render time
        align: 'center',
        gap: 18,
        fontSize: 13,
        color: '#a1a1aa',
        paddingY: 0,
        paddingX: 0,
      };
    default:
      throw new Error(`Unknown page leaf type: ${type}`);
  }
}

// ── Legacy block migration ────────────────────────────────────────────────────

// Convert a legacy standalone `video` leaf into a `library` leaf (the video block
// was folded into File/Folder/Embed). A file-provider video becomes a file-mode
// library block (mime forces the video viewer); a youtube/vimeo video becomes an
// embed-mode block. Keeps the SAME id so selection/blockmap continuity holds.
function videoLeafToLibrary(v) {
  const base = {
    ...createPageLeaf('library'),
    id: v.id,
    width: v.width ?? 100,
    align: v.align ?? 'center',
    autoplay: !!v.autoplay,
    loop: !!v.loop,
    muted: !!v.muted,
    controls: v.controls !== false,
  };
  if (v.provider === 'youtube' || v.provider === 'vimeo') {
    return { ...base, mode: 'embed', embedProvider: v.provider, embedId: v.src || '' };
  }
  // file-provider: route to the library's video viewer via a video/* mime.
  return { ...base, mode: 'file', src: v.src || '', filename: 'video.mp4', mime: 'video/mp4' };
}

// Convert a legacy standalone `image` leaf into a `library` leaf (file mode).
// `banner` has no LibraryBlock equivalent — it's converted into concrete
// values (full width, zero padding) rather than carried forward as a concept,
// since nothing downstream needs to know "this was a banner" once converted.
// href/caption/showCaption DO carry through as first-class LibraryBlock
// fields (see pageBlocks.d.ts) so click-through links and captions survive
// with zero feature loss. Keeps the SAME id so selection/blockmap continuity
// holds, mirroring videoLeafToLibrary above.
function imageLeafToLibrary(img) {
  const banner = !!img.banner;
  return {
    ...createPageLeaf('library'),
    id: img.id,
    mode: 'file',
    src: img.src || '',
    filename: '',
    mime: 'image/', // sentinel — same convention buildMergedGallery uses for Shopify images
    width: banner ? 100 : (img.width ?? 100),
    align: img.align ?? 'center',
    // Legacy imageBlockStyle: banner → 0 padding; else a FIXED 32px horizontal
    // pad regardless of `spacing` (spacing only ever controlled the vertical).
    paddingY: banner ? 0 : (img.spacing ?? 0),
    paddingX: banner ? 0 : 32,
    href: img.href || '',
    caption: img.caption || '',
    showCaption: !!img.showCaption,
    alt: img.alt || '',
    sectionColor: img.sectionColor || 'transparent',
  };
}

// Upgrade legacy leaf types in a doc to their current equivalents (the old
// standalone `video` and `image` blocks → `library`). Returns the SAME doc
// object when nothing changed, so callers can skip a needless save.
export function migrateLegacyBlocks(doc) {
  if (!doc || !Array.isArray(doc.sections)) return doc;
  let changed = false;
  const sections = doc.sections.map((s) => ({
    ...s,
    rows: (s.rows || []).map((r) => ({
      ...r,
      columns: (r.columns || []).map((c) => ({
        ...c,
        blocks: (c.blocks || []).map((b) => {
          if (b && b.type === 'video') { changed = true; return videoLeafToLibrary(b); }
          if (b && b.type === 'image') { changed = true; return imageLeafToLibrary(b); }
          return b;
        }),
      })),
    })),
  }));
  return changed ? { ...doc, sections } : doc;
}

// ── Container factories ──────────────────────────────────────────────────────

export function createColumn(blocks = [], widthFraction = 1) {
  return { id: genId('col'), type: 'column', widthFraction, blocks };
}

export function createRow(columns) {
  return {
    id: genId('row'), type: 'row',
    columns: columns && columns.length ? columns : [createColumn()],
    gap: 24,
    align: 'top', // 'top' | 'center' | 'stretch'
    stackOnMobile: true,
  };
}

export function createSection(rows) {
  return {
    id: genId('sec'), type: 'section',
    bgColor: 'transparent',
    bgImage: '',
    paddingY: 64,
    paddingX: 24,
    fullBleed: false,
    maxWidth: PAGE_DEFAULT_THEME.contentMaxWidth,
    rows: rows && rows.length ? rows : [createRow()],
  };
}

// Convenience: a section holding a single full-width leaf block.
function sectionWith(leaf, overrides = {}) {
  return { ...createSection([createRow([createColumn([leaf])])]), ...overrides };
}

// A mini-nav link (for the mini-nav block, e.g. in the footer).
export function createMiniNavLink(label = 'Link', target = '') {
  return { id: genId('mnl'), label, target };
}

// Pull an 11-char YouTube id out of a watch / youtu.be / embed / shorts / live
// URL, or accept a bare id. Returns '' when nothing matches so the caller can
// reject the input. Kept here (shared) so the renderer and the picker agree.
export function parseYouTubeId(input) {
  const s = String(input || '').trim();
  if (/^[\w-]{11}$/.test(s)) return s;
  const m = s.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/|v\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : '';
}

// Pull a Vimeo id from a vimeo.com / player.vimeo.com URL (or accept a bare
// numeric id). Unlisted videos carry a privacy hash (the `h` param or a trailing
// path segment) — when present it's returned as `id/hash` so the renderer can
// add `?h=` to the embed. Returns '' when nothing matches.
export function parseVimeoId(input) {
  const s = String(input || '').trim();
  if (/^\d+$/.test(s)) return s;
  // player embed: player.vimeo.com/video/<id>?h=<hash>
  let m = s.match(/player\.vimeo\.com\/video\/(\d+)(?:[^#]*?[?&]h=([a-zA-Z0-9]+))?/);
  if (m) return m[2] ? `${m[1]}/${m[2]}` : m[1];
  // watch/channels/groups: vimeo.com[/channels/x|/groups/x/videos|/video]/<id>[/<hash>]
  m = s.match(/vimeo\.com\/(?:channels\/[\w-]+\/|groups\/[\w-]+\/videos\/|video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/);
  if (m) return m[2] ? `${m[1]}/${m[2]}` : m[1];
  return '';
}

// The permanent footer: a pinned (isFooter) section holding a mini-nav. Every
// page has exactly one; the editor blockmap shows it pinned (no delete/drag).
export function createFooterSection() {
  const sec = sectionWith(createPageLeaf('mini-nav'), { bgColor: '#0a0a0a', paddingY: 36 });
  sec.isFooter = true;
  return sec;
}

// Drop any footer section(s) from a doc. The footer is a SITE-WIDE shared
// section (stored per-owner, like the nav) rather than per-page, so a page's own
// `sections` must never carry one — strip before saving a page.
export function stripFooter(doc) {
  return { ...doc, sections: (doc.sections || []).filter((s) => !s.isFooter) };
}

// Compose a renderable/editable doc: the page's own sections with the shared
// footer pinned last. Strips any stale embedded footer first (legacy pages saved
// before the footer became shared), then appends the shared one. `footer` is a
// SectionBlock; falls back to a fresh default footer when absent.
export function withSharedFooter(doc, footer) {
  const base = (doc.sections || []).filter((s) => !s.isFooter);
  const f = { ...(footer || createFooterSection()), isFooter: true };
  return { ...doc, sections: [...base, f] };
}

// Apply the SITE-WIDE theme to a doc, overriding whatever per-page theme the doc
// carries. The theme is stored once per owner (like the nav/footer), so every
// page renders with the same colors/fonts/layout. Missing fields fall back to
// the defaults so an older/partial saved theme can't drop a property.
export function withSharedTheme(doc, theme) {
  return { ...doc, theme: { ...PAGE_DEFAULT_THEME, ...(theme || {}) } };
}

// ── Document factories ───────────────────────────────────────────────────────

export function createDefaultDoc(slug = 'home', title = 'Untitled page') {
  return {
    slug,
    title,
    seoDescription: '',
    theme: { ...PAGE_DEFAULT_THEME },
    // No footer here — the footer is a site-wide shared section appended at
    // render/edit time (withSharedFooter), so every page shows the same one.
    sections: [
      sectionWith(createPageLeaf('hero')),
      createSection([createRow([createColumn([createPageLeaf('text')])])]),
    ],
  };
}

// The storefront landing page: hero band + a full product grid. Used to seed
// each org's /g/:slug landing so the route works before any edit.
export function createHomePageDoc() {
  const hero = createPageLeaf('hero');
  hero.heading = 'Sleepy Circuits';
  hero.subheading = 'Hypnotic shaders, video packs, and more.';
  hero.ctaLabel = 'Shop the collection';

  const grid = createPageLeaf('product-grid');
  grid.handles = 'all';

  return {
    slug: 'home',
    title: 'Sleepy Circuits',
    seoDescription: 'Hypnotic shaders, video packs, and more from Sleepy Circuits.',
    theme: { ...PAGE_DEFAULT_THEME },
    sections: [
      sectionWith(hero, { paddingY: 0, paddingX: 0, fullBleed: true }),
      sectionWith(grid),
    ],
  };
}

// The default product page: a single product detail embed + a related grid.
// Used both as the lazy-seed starting point in the builder and as the public
// render fallback for products that have no builder page row.
export function createProductPageDoc(handle, productId = null) {
  const embed = createPageLeaf('product-embed');
  embed.handle = handle;
  if (productId) embed.productId = productId;
  embed.variant = 'detail';

  const grid = createPageLeaf('product-grid');
  grid.handles = 'all';

  return {
    slug: handle,
    // Blank, not the handle — this isn't a real title, just where the page
    // lives. The server (resolvePublicTitle/siteSeo) and the builder's
    // ProductPageInspector both treat blank as "show the live Shopify product
    // title"; baking the handle in here made every fresh product page's
    // inspector show it as if it were a deliberately-set title.
    title: '',
    seoDescription: '',
    theme: { ...PAGE_DEFAULT_THEME },
    sections: [
      // The unified header + gallery + buy box: flush under the site nav (no
      // section padding, matching the home hero) and pinned (isProductHeader).
      sectionWith(embed, { paddingY: 0, paddingX: 0, fullBleed: true, isProductHeader: true }),
      sectionWith(grid, { bgColor: '#111113' }),
    ],
  };
}

// Guarantee a product page has its pinned header section (the product-embed
// detail) at the very top, gap-free and locked. Idempotent: if a section already
// holds a product-embed detail leaf, normalize it in place (flag it, drop its
// padding, sync the handle, hoist to index 0) rather than prepend a duplicate.
// Otherwise prepend a fresh header. Used by the editor when opening a product page.
export function ensureProductHeader(doc, handle, productId = null) {
  const sections = (doc.sections || []).slice();
  const isDetailEmbed = (s) => {
    const leaf = s.rows?.[0]?.columns?.[0]?.blocks?.[0];
    return leaf && leaf.type === 'product-embed' && leaf.variant === 'detail';
  };
  const idx = sections.findIndex((s) => s.isProductHeader || isDetailEmbed(s));
  if (idx >= 0) {
    let header = sections[idx];
    const embed = header.rows[0].columns[0].blocks[0];
    // Preserve an existing productId; adopt the passed one when the block lacks
    // it (lazy backfill of legacy handle-only blocks).
    const nextEmbed = { ...embed, handle, variant: 'detail' };
    if (productId || embed.productId) nextEmbed.productId = embed.productId || productId;
    header = {
      ...header,
      isProductHeader: true,
      paddingY: 0, paddingX: 0, fullBleed: true,
      rows: header.rows.map((r, ri) => (ri === 0
        ? { ...r, columns: r.columns.map((c, ci) => (ci === 0
            ? { ...c, blocks: c.blocks.map((b, bi) => (bi === 0 ? nextEmbed : b)) }
            : c)) }
        : r)),
    };
    sections.splice(idx, 1);
    sections.unshift(header);
    return { ...doc, sections };
  }
  const embed = createPageLeaf('product-embed');
  embed.handle = handle;
  if (productId) embed.productId = productId;
  embed.variant = 'detail';
  const header = sectionWith(embed, { paddingY: 0, paddingX: 0, fullBleed: true, isProductHeader: true });
  return { ...doc, sections: [header, ...sections] };
}

// ── Site navigation (editable top bar) ───────────────────────────────────────
// A SiteNav is site-wide (shared across pages): brand, logo, and an ordered list
// of items. An item is either a 'link' (to a page slug or external URL) or a
// 'products' dropdown that auto-lists every Shopify product at render time.

export function createNavLink(label, target) {
  return { id: genId('nav'), type: 'link', label, target };
}

export function createNavProducts(label = 'Shop') {
  return { id: genId('nav'), type: 'products', label };
}

// A child link inside a custom dropdown.
export function createNavChild(label = 'Link', target = '') {
  return { id: genId('navc'), label, target };
}

// A custom collapsible dropdown nav item with editable child links.
export function createNavDropdown(label = 'Menu') {
  return { id: genId('nav'), type: 'dropdown', label, children: [createNavChild('Link', 'home')] };
}

export const DEFAULT_NAV_STYLE = Object.freeze({
  bgColor: 'rgba(10,10,10,0.9)',
  textColor: '#e4e4e7',
  fontSize: 14,
  height: 64,
  logoSize: 36,
});

/**
 * The logo a site's loading spinner shows: the nav's lightweight variant when one is ready (attached server-side,
 * server/mediaCdn.js attachSiteMediaVariants), else the nav logo itself. Only http(s) or same-origin URLs; '' means
 * the default Sleepy logo.
 */
export function navSplashLogo(nav) {
  const url = String(nav?.logoUrlVariantSrc || nav?.logoUrl || '').trim();
  return /^(https?:\/\/|\/(?!\/))/i.test(url) ? url : '';
}

export function createDefaultNav() {
  return {
    brandName: 'Sleepy Circuits',
    logoUrl: '',
    style: { ...DEFAULT_NAV_STYLE },
    items: [
      createNavLink('Home', 'home'),
      createNavProducts('Shop'),
    ],
  };
}

// ── Validation ───────────────────────────────────────────────────────────────

const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// Normalizes free text into a URL-safe slug: lowercase, every non [a-z0-9-]
// char becomes a dash, leading/trailing dashes trimmed. Shared by every place
// a slug is derived from user input (new-page prompt, slug rename, the
// inspector's Page URL field) so they can never disagree on what's "safe".
export const slugify = (raw) => String(raw).trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/^-+|-+$/g, '');

// Auto-derived display title from a slug ("return-policy" -> "Return Policy").
// Isomorphic so the client (page list/sidebar) and server (resolvePublicTitle,
// SSR <title>) always agree on what an "auto" title looks like.
export const titleCaseSlug = (slug) => String(slug)
  .split('-').filter(Boolean)
  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
  .join(' ');

// Returns an array of human-readable problems; empty = valid. Mirrors
// newsletterBlocks.validateDoc's shape so the route can 400 with the list.
export function validatePageDoc(doc) {
  const errors = [];
  if (!doc || typeof doc !== 'object') return ['Document is missing.'];
  const slug = String(doc.slug || '').trim();
  if (!slug) errors.push('A page slug is required.');
  else if (!SLUG_RE.test(slug)) errors.push('Slug must be lowercase letters, numbers, and dashes.');
  if (!Array.isArray(doc.sections)) errors.push('Page sections must be a list.');
  else {
    for (const sec of doc.sections) {
      if (!sec || sec.type !== 'section') { errors.push('Every top-level block must be a section.'); break; }
      if (!Array.isArray(sec.rows)) { errors.push('A section is missing its rows.'); break; }
    }
  }
  return errors;
}

// ── URL safety ───────────────────────────────────────────────────────────────
// Homepages are authored by any account and served on the app's own origin
// (/g/:slug), so a URL in a doc must never be able to run script there: a
// library block's `src` lands in an <iframe src> (PDF) and an <a href>
// (download), and React 18 renders `javascript:` URLs as given. Every URL a
// doc carries passes this policy on save AND on the public read (docs stored
// before it existed), and the renderers check it again before using one.

// The keys whose string values are URLs or link targets (block, section,
// nav, footer and gallery fields; the derived *Src/*Url variant fields too).
const URL_KEYS = new Set(['src', 'href', 'target', 'ctaHref', 'poster', 'bgImage', 'logoUrl', 'url']);
const URL_KEY_SUFFIX_RE = /(Src|Url|Href)$/;
// Schemes a link or media URL may use. `gid:` is a Shopify product id a link
// target carries (the storefront resolves it to a page; it never becomes an href).
const SAFE_URL_SCHEMES = new Set(['http', 'https', 'mailto', 'tel', 'gid']);
const SAFE_DATA_IMAGE_RE = /^data:image\/(png|jpe?g|gif|webp|avif);/i;
// Browsers ignore ASCII whitespace/control characters inside a scheme
// ("java\tscript:"), so the check runs on the URL with them removed.
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const URL_IGNORED_CHARS_RE = /[\u0000-\u0020\u007f-\u009f]+/g;

/**
 * Whether a URL (or link target: a page slug, '#anchor', '/path') is safe to
 * put in an href/src on our origin: no scheme, or http(s)/mailto/tel/gid, or a
 * raster data:image. Empty is safe (it renders nothing).
 */
export function isSafeSiteUrl(value) {
  const compact = String(value ?? '').replace(URL_IGNORED_CHARS_RE, '');
  if (!compact) return true;
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(compact);
  if (!scheme) return true;
  const name = scheme[1].toLowerCase();
  if (name === 'data') return SAFE_DATA_IMAGE_RE.test(compact);
  return SAFE_URL_SCHEMES.has(name);
}

const isUrlKey = (key) => URL_KEYS.has(key) || URL_KEY_SUFFIX_RE.test(key);

/**
 * A copy of `value` (a PageDoc, section, nav or any JSON) with every unsafe
 * URL emptied (see isSafeSiteUrl). Walks the whole tree, so a block type added
 * later is covered without listing it here. Non-objects come back as given.
 */
export function sanitizePageDocUrls(value) {
  if (Array.isArray(value)) return value.map(sanitizePageDocUrls);
  if (!value || typeof value !== 'object') return value;
  const out = {};
  for (const [key, v] of Object.entries(value)) {
    if (typeof v === 'string') out[key] = isUrlKey(key) && !isSafeSiteUrl(v) ? '' : v;
    else out[key] = sanitizePageDocUrls(v);
  }
  return out;
}

// ── Tree helpers (read-only) ─────────────────────────────────────────────────

// Depth-first iterate every leaf block in the tree, calling fn(leaf, column,
// row, section). Used by the renderer/builder without re-walking by hand.
export function forEachLeaf(doc, fn) {
  for (const section of doc.sections || []) {
    for (const row of section.rows || []) {
      for (const column of row.columns || []) {
        for (const leaf of column.blocks || []) {
          fn(leaf, column, row, section);
        }
      }
    }
  }
}

const MEDIA_ID_RE = /([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

/**
 * Walk a PageDoc, collecting every distinct `/api/media-uploads/:id` reference
 * an active block actually renders, as `id -> mime` (first-seen mime wins).
 * Isomorphic — the ONE canonical media-reference walk, used both server-side
 * (media-variant CDN attachment, "is this media used on the site" lookup) and
 * client-side (the site-builder's lightweight-variant backfill scan), so the
 * leaf-shape inventory below can never drift between the two.
 *
 * Covers: section/hero `bgImage` (the footer is itself passed in as a
 * one-section doc by callers, so it's covered by the same loop); `library`
 * leaf file-mode `src`+`mime`; legacy `image` leaf `src`; legacy standalone
 * `video` leaf (file provider) `src`+`poster`; `product-embed`'s own-uploaded
 * `extraImages[]` `src`+`poster`.
 *
 * Explicitly NOT covered: `library` folder mode (a whole browsable subtree —
 * a different-shaped operation than a single-doc walk) and `product-grid`
 * (Shopify-hosted images only, never our media).
 */
export function collectDocMediaRefs(doc, out = new Map()) {
  const note = (src, mime) => {
    if (!src) return;
    const m = MEDIA_ID_RE.exec(src);
    if (m && !out.has(m[1])) out.set(m[1], mime || 'image/jpeg');
  };
  for (const section of doc.sections || []) note(section.bgImage);
  forEachLeaf(doc, (leaf) => {
    if (leaf.type === 'hero') note(leaf.bgImage);
    else if (leaf.type === 'library' && leaf.mode === 'file') note(leaf.src, leaf.mime);
    else if (leaf.type === 'image') note(leaf.src);
    else if (leaf.type === 'video' && leaf.provider === 'file') { note(leaf.src, 'video/mp4'); note(leaf.poster); }
    else if (leaf.type === 'product-embed') {
      for (const img of leaf.extraImages || []) { note(img.src, img.mime); note(img.poster); }
    }
  });
  return out;
}

// Build the merged gallery for a product-embed detail block: the live Shopify
// product images (when enabled) interleaved with the block's own B2 images,
// ordered by `galleryOrder`. Pure — reused by PageRenderer and EditableCanvas so
// the canvas preview matches the published render.
//
// Image keys: Shopify images are keyed 'shopify:<index>' (stable by Storefront
// order); extra images by their own id. Any key absent from `galleryOrder` is
// appended in natural order (Shopify first, then extras), so a newly-added
// Shopify image is never dropped just because the saved order predates it.
export function buildMergedGallery(product, opts = {}) {
  const { showShopifyImages = true, extraImages = [], galleryOrder = [] } = opts;
  const byKey = new Map();
  if (showShopifyImages) {
    (product?.images || []).forEach((img, i) => {
      byKey.set(`shopify:${i}`, { key: `shopify:${i}`, src: img.src, altText: img.altText ?? null, mime: 'image/', filename: '', poster: null });
    });
  }
  for (const ex of extraImages || []) {
    if (!ex || !ex.id || !ex.src) continue;
    // Carry mime/filename so the gallery classifies the item (image / video /
    // 3D model / pdf / …) via getPreviewKind. Fall back to the legacy `type`
    // flag for any item saved before mime/filename were stored.
    const mime = ex.mime || (ex.type === 'video' ? 'video/' : '');
    // variantSrc/posterVariantSrc are server-attached siblings on `ex`
    // (server/mediaCdn.js attachSiteMediaVariants), not authored fields —
    // carried through here so the storefront gallery can actually use them;
    // previously dropped by this object-literal whitelist, silently pinning
    // every gallery image to its full original regardless of variant status.
    byKey.set(ex.id, {
      key: ex.id, src: ex.src, altText: ex.alt ?? null, mime, filename: ex.filename || '', poster: ex.poster || null,
      variantPlaceholderSrc: ex.variantPlaceholderSrc || null,
      variantSrc: ex.variantSrc || null, variantLgSrc: ex.variantLgSrc || null, posterVariantSrc: ex.posterVariantSrc || null,
      videoVariantSrc: ex.videoVariantSrc || null,
    });
  }
  const out = [];
  const taken = new Set();
  for (const key of galleryOrder || []) {
    if (byKey.has(key) && !taken.has(key)) { out.push(byKey.get(key)); taken.add(key); }
  }
  for (const [key, img] of byKey) {
    if (!taken.has(key)) out.push(img);
  }
  return out;
}

// Map each variant id to the INDEX of its hero image within a built merged
// gallery, so ProductDetail can advance the gallery when that variant is picked.
// Skips heroes whose key isn't in the gallery (e.g. a hidden Shopify image).
export function buildVariantHeroIndex(mergedGallery, variantHeroImages) {
  const out = {};
  if (!variantHeroImages) return out;
  const keyToIdx = new Map((mergedGallery || []).map((m, i) => [m.key, i]));
  for (const [variantId, key] of Object.entries(variantHeroImages)) {
    const idx = keyToIdx.get(key);
    if (idx != null) out[variantId] = idx;
  }
  return out;
}

// Build the immutable patch that appends one extra (our-own-bucket) media item
// to a product-embed's gallery: a new {id, src, alt, mime, filename} in
// extraImages plus its key at the end of galleryOrder. mime/filename let the
// gallery classify the item (image / video / 3D model / pdf / …) via the shared
// getPreviewKind, so any supported file renders through one preview path. Shared
// by the canvas paperclip and the inspector list so both add media the same way.
export function buildExtraImagePatch(embed, src, opts = {}) {
  const id = genId('img');
  const item = { id, src, alt: '', mime: opts.mime || '', filename: opts.filename || '' };
  if (opts.poster) item.poster = opts.poster;
  return {
    extraImages: [...(embed.extraImages || []), item],
    galleryOrder: [...(embed.galleryOrder || []), id],
  };
}

// Collect the distinct product handles referenced by product-embed blocks (so a
// renderer can prefetch only the products a page actually needs).
export function referencedProductHandles(doc) {
  const handles = new Set();
  forEachLeaf(doc, (leaf) => {
    if (leaf.type === 'product-embed' && leaf.handle) handles.add(leaf.handle);
  });
  return [...handles];
}

// ── Tree mutation (immutable; for the builder) ───────────────────────────────
// Every block (section/row/column/leaf) carries a unique `id`, so the builder
// addresses edits by id and these helpers rebuild only the touched path.

export function findBlockById(doc, id) {
  let found = null;
  for (const section of doc.sections || []) {
    if (section.id === id) return section;
    for (const row of section.rows || []) {
      if (row.id === id) return row;
      for (const column of row.columns || []) {
        if (column.id === id) return column;
        for (const leaf of column.blocks || []) {
          if (leaf.id === id) return leaf;
        }
      }
    }
  }
  return found;
}

// Return a new doc with the block `id` shallow-merged with `patch`. Works at any
// level. No-op (returns an equal-shaped new doc) if the id isn't found.
export function updateBlockById(doc, id, patch) {
  const mapLeaf = (leaf) => (leaf.id === id ? { ...leaf, ...patch } : leaf);
  const mapColumn = (col) => {
    const next = col.id === id ? { ...col, ...patch } : col;
    return { ...next, blocks: (next.blocks || []).map(mapLeaf) };
  };
  const mapRow = (row) => {
    const next = row.id === id ? { ...row, ...patch } : row;
    return { ...next, columns: (next.columns || []).map(mapColumn) };
  };
  const mapSection = (sec) => {
    const next = sec.id === id ? { ...sec, ...patch } : sec;
    return { ...next, rows: (next.rows || []).map(mapRow) };
  };
  return { ...doc, sections: (doc.sections || []).map(mapSection) };
}

// Remove a block (section/row/column/leaf) by id. Guards against emptying a
// section of all rows or a row of all columns (keeps the tree renderable).
export function removeBlockById(doc, id) {
  const sections = [];
  for (const sec of doc.sections || []) {
    if (sec.id === id) continue;
    const rows = [];
    for (const row of sec.rows || []) {
      if (row.id === id) continue;
      const columns = [];
      for (const col of row.columns || []) {
        if (col.id === id) continue;
        columns.push({ ...col, blocks: (col.blocks || []).filter((l) => l.id !== id) });
      }
      if (columns.length) rows.push({ ...row, columns });
    }
    if (rows.length) sections.push({ ...sec, rows });
  }
  return { ...doc, sections };
}

// Append a new leaf block to a column.
export function addLeafToColumn(doc, columnId, leafType) {
  const leaf = createPageLeaf(leafType);
  return updateColumnBlocks(doc, columnId, (blocks) => [...blocks, leaf]);
}

// Reorder leaves within a column (drag-and-drop).
export function moveLeafInColumn(doc, columnId, fromIdx, toIdx) {
  return updateColumnBlocks(doc, columnId, (blocks) => {
    const next = blocks.slice();
    const [moved] = next.splice(fromIdx, 1);
    if (moved) next.splice(toIdx, 0, moved);
    return next;
  });
}

function updateColumnBlocks(doc, columnId, fn) {
  return {
    ...doc,
    sections: (doc.sections || []).map((sec) => ({
      ...sec,
      rows: (sec.rows || []).map((row) => ({
        ...row,
        columns: (row.columns || []).map((col) =>
          col.id === columnId ? { ...col, blocks: fn(col.blocks || []) } : col),
      })),
    })),
  };
}

// Append a new section (with `columnCount` equal columns) to the doc.
export function addSection(doc, columnCount = 1) {
  const columns = Array.from({ length: Math.max(1, columnCount) }, () => createColumn([], 1 / Math.max(1, columnCount)));
  const newSec = createSection([createRow(columns)]);
  const sections = (doc.sections || []).slice();
  // Insert before the pinned footer (if present) so the footer stays last.
  const footerIdx = sections.findIndex((s) => s.isFooter);
  if (footerIdx >= 0) sections.splice(footerIdx, 0, newSec);
  else sections.push(newSec);
  return { ...doc, sections };
}

// Append a row (with `columnCount` equal columns) to a section.
export function addRowToSection(doc, sectionId, columnCount = 1) {
  const columns = Array.from({ length: Math.max(1, columnCount) }, () => createColumn([], 1 / Math.max(1, columnCount)));
  return {
    ...doc,
    sections: (doc.sections || []).map((sec) =>
      sec.id === sectionId ? { ...sec, rows: [...(sec.rows || []), createRow(columns)] } : sec),
  };
}

// Deep-clone a block subtree with fresh ids (so a duplicate is fully independent).
function cloneLeaf(leaf) { return { ...leaf, id: genId(leaf.type) }; }
function cloneColumn(col) { return { ...col, id: genId('col'), blocks: (col.blocks || []).map(cloneLeaf) }; }
function cloneRow(row) { return { ...row, id: genId('row'), columns: (row.columns || []).map(cloneColumn) }; }
function cloneSection(sec) { return { ...sec, id: genId('sec'), rows: (sec.rows || []).map(cloneRow) }; }

// Duplicate a section or leaf block by id, inserting the copy right after the
// original. No-op if the id isn't found.
export function duplicateBlockById(doc, id) {
  const secIdx = (doc.sections || []).findIndex((s) => s.id === id);
  if (secIdx >= 0) {
    const sections = doc.sections.slice();
    sections.splice(secIdx + 1, 0, cloneSection(doc.sections[secIdx]));
    return { ...doc, sections };
  }
  let done = false;
  return {
    ...doc,
    sections: (doc.sections || []).map((sec) => ({
      ...sec,
      rows: (sec.rows || []).map((row) => ({
        ...row,
        columns: (row.columns || []).map((col) => {
          if (done) return col;
          const idx = (col.blocks || []).findIndex((b) => b.id === id);
          if (idx < 0) return col;
          done = true;
          const blocks = col.blocks.slice();
          blocks.splice(idx + 1, 0, cloneLeaf(col.blocks[idx]));
          return { ...col, blocks };
        }),
      })),
    })),
  };
}

// Reorder top-level sections (drag-and-drop in the page tree).
export function moveSection(doc, fromIdx, toIdx) {
  const sections = (doc.sections || []).slice();
  const [moved] = sections.splice(fromIdx, 1);
  if (moved) sections.splice(toIdx, 0, moved);
  return { ...doc, sections };
}

// Move a leaf block to a target column at a target index. Handles both
// same-column reordering and moving a block across columns/sections (drag-and-
// drop in the page tree). No-op if the leaf isn't found.
export function moveLeafTo(doc, leafId, targetColumnId, targetIndex) {
  let moved = null;
  // 1) Remove the leaf from wherever it currently lives.
  const stripped = (doc.sections || []).map((sec) => ({
    ...sec,
    rows: (sec.rows || []).map((row) => ({
      ...row,
      columns: (row.columns || []).map((col) => {
        const idx = (col.blocks || []).findIndex((b) => b.id === leafId);
        if (idx < 0) return col;
        moved = col.blocks[idx];
        return { ...col, blocks: col.blocks.filter((b) => b.id !== leafId) };
      }),
    })),
  }));
  if (!moved) return doc;
  // 2) Insert it into the target column at the target index.
  return {
    ...doc,
    sections: stripped.map((sec) => ({
      ...sec,
      rows: (sec.rows || []).map((row) => ({
        ...row,
        columns: (row.columns || []).map((col) => {
          if (col.id !== targetColumnId) return col;
          const blocks = (col.blocks || []).slice();
          const i = Math.max(0, Math.min(targetIndex, blocks.length));
          blocks.splice(i, 0, moved);
          return { ...col, blocks };
        }),
      })),
    })),
  };
}
